<script setup lang="ts">
import { Check, XMark, Explamationmark } from "@/components/icons";
import { NotificationPayload, notifyEmitter } from "@/lib/notify";
import { onMounted, onUnmounted, ref } from "vue";
import { v4 as uuidv4 } from "uuid";
import {
  ToastProvider,
  ToastRoot,
  ToastViewport,
  ToastTitle,
  ToastDescription,
  ToastClose,
} from "reka-ui";

const list = ref<({ id: string; open: boolean } & NotificationPayload)[]>([]);

const addItem = (event: NotificationPayload) => {
  list.value.push({ id: uuidv4(), open: true, ...event });
};

onMounted(() => {
  notifyEmitter.on("add", addItem);
});

onUnmounted(() => {
  notifyEmitter.off("add", addItem);
});
</script>

<template>
  <ToastProvider :duration="8000" swipeDirection="right">
    <ToastRoot
      v-for="value in list"
      :key="value.id"
      :class="[
        'bg-background-primary shadow-card border border-border-primary rounded-xl',
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:slide-out-to-right data-[state=open]:fade-in data-[state=open]:slide-in-from-right',
        'data-[swipe=move]:translate-x-(--reka-toast-swipe-move-x) data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-[transform_200ms_ease-out] data-[swipe=end]:animate-swipe-out',
      ]"
      v-model:open="value.open"
    >
      <ToastClose class="p-3 flex items-center text-left">
        <div
          class="bg-background-brand text-icon-white flex-center w-12 h-12 rounded-full mr-2 shrink-0"
        >
          <Explamationmark v-if="value.type === 'warning'" size="32" />
          <XMark v-else-if="value.type === 'error'" size="32" />
          <Check v-else-if="value.type === 'success'" size="32" />
        </div>
        <div>
          <ToastTitle class="font-bold leading-5">{{ value.title }}</ToastTitle>
          <ToastDescription
            class="opacity-60 leading-5 mt-1"
            v-html="value.text"
          />
        </div>
      </ToastClose>
    </ToastRoot>
    <ToastViewport
      class="fixed bottom-0 right-0 flex flex-col p-4 gap-2 w-80 max-w-screen m-0 list-none z-[5000] outline-none"
    />
  </ToastProvider>
</template>
